// SPDX-License-Identifier: MIT
/** FNV-1a 32-bit hash of a string, mixed with an optional seed. */
export function hashString(s: string, seed = 0x811c9dc5): number {
    let h = seed >>> 0;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
}
/** mulberry32: small, fast, deterministic PRNG returning floats in [0, 1). */
export function mulberry32(seed: number): () => number {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6d2b79f5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/**
 * Keyed RNG factory: `rng(key)` always returns a fresh generator in the same state for the same
 * (seed, namespace, key), so drawing code stays a pure function of time.
 */
export function createRng(seed: number, namespace = ''): (key: string) => () => number {
    const base = hashString(namespace, seed >>> 0 || 1);
    return (key) => mulberry32(hashString(key, base));
}
/** Stateless integer hash → [0, 1). Useful for per-element noise without allocating generators. */
export function hash01(a: number, b = 0, c = 0): number {
    let h = Math.imul(a | 0, 0x27d4eb2d) ^ Math.imul(b | 0, 0x165667b1) ^ Math.imul(c | 0, 0x9e3779b1);
    h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
    h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
    return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}
